import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import useUnsavedWarning from '../../../hooks/useUnsavedWarning';
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import { confirmDialog } from "../../../utils/confirm";
import { Plus, Trash2 } from "lucide-react";
import { getNonRefundSections, saveNonRefundSections } from "../../../api";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";

const ICON_OPTIONS = ["Shield", "Database", "Settings", "CheckCircle", "Mail"];

const AdminCMSNonRefundPolicy = () => {
  const [sections, setSections] = useState([]);
  const [originalSections, setOriginalSections] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Validation & Change tracking
  const isFormInvalid = useMemo(() => {
    if (sections.length === 0) return false;
    return sections.some((s) => !s.title?.trim() || !s.content?.trim());
  }, [sections]);

  const isChanged = useMemo(() => {
    if (sections.length !== originalSections.length) return true;
    return sections.some((s, i) => {
      const orig = originalSections[i];
      if (
        s.title !== orig.title ||
        s.content !== orig.content ||
        s.iconName !== orig.iconName
      )
        return true;

      const currList = s.listItems || [];
      const origList = orig.listItems || [];
      if (currList.length !== origList.length) return true;
      return currList.some((item, j) => item !== origList[j]);
    });
  }, [sections, originalSections]);
  useUnsavedWarning(isChanged);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const stored = await getNonRefundSections();
        if (!mounted) return;
        const data = (Array.isArray(stored) ? stored : []).map((s) => ({
          ...s,
          listItems: s.listItems ? [...s.listItems] : [],
          titleTouched: true,
          contentTouched: true,
        }));
        setSections(JSON.parse(JSON.stringify(data)));
        setOriginalSections(JSON.parse(JSON.stringify(data)));
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load Non Refund Policy",
        );
        setSections([]);
        setOriginalSections([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const addSection = () => {
    const id = `section-${Date.now()}`;
    setSections([
      ...sections,
      {
        id,
        title: "",
        iconName: "Shield",
        content: "",
        listItems: [],
        titleTouched: false,
        contentTouched: false,
      },
    ]);
  };

  const updateSection = (index, field, value) => {
    const next = [...sections];
    next[index] = {
      ...next[index],
      [field]: value,
      [`${field}Touched`]: true,
    };
    setSections(next);
  };

  const updateListItem = (sectionIndex, itemIndex, value) => {
    const next = [...sections];
    const list = [...(next[sectionIndex].listItems || [])];
    list[itemIndex] = value;
    next[sectionIndex] = { ...next[sectionIndex], listItems: list };
    setSections(next);
  };

  const addListItem = (sectionIndex) => {
    const next = [...sections];
    const list = [...(next[sectionIndex].listItems || [])];
    list.push("");
    next[sectionIndex] = { ...next[sectionIndex], listItems: list };
    setSections(next);
  };

  const removeListItem = (sectionIndex, itemIndex) => {
    const next = [...sections];
    const list = (next[sectionIndex].listItems || []).filter(
      (_, i) => i !== itemIndex,
    );
    next[sectionIndex] = { ...next[sectionIndex], listItems: list };
    setSections(next);
  };

  const removeSection = async (index) => {
    const confirmed = await confirmDialog({
      title: "Remove section?",
      text: "This section will be removed from Non Refund Policy.",
    });
    if (!confirmed) return;
    setSections(sections.filter((_, i) => i !== index));
    Swal.fire({
      icon: "success",
      title: "Removed",
      text: "This Section removed successfully.",
      confirmButtonColor: "#38915c",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const handleSave = async () => {
    if (isFormInvalid) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Sections",
        text: "Please fill in all titles and content before saving.",
        confirmButtonColor: "#38915c",
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await saveNonRefundSections(sections);
      const data = (Array.isArray(saved) ? saved : sections).map((s) => ({
        ...s,
        listItems: s.listItems ? [...s.listItems] : [],
      }));
      setSections(JSON.parse(JSON.stringify(data)));
      setOriginalSections(JSON.parse(JSON.stringify(data)));
      setError("");
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Non-Refund Policy updated successfully",
        confirmButtonColor: "#38915c",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          err.message ||
          "Failed to save Non Refund Policy",
        confirmButtonColor: "#38915c",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link
            to="/admin/cms"
            className="text-sm text-primary-600 hover:underline mb-1 inline-block"
          >
            ← CMS Pages
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">
            Non Refund Policy
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage refund policy details. All fields are required.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {sections.length === 0 && (
          <div className="p-6 rounded-xl border-2 border-dashed border-gray-200 bg-white text-gray-600">
            -
          </div>
        )}
        {sections.map((section, index) => (
          <div
            key={section.id}
            className="p-6 rounded-xl border-2 border-gray-200 bg-white"
          >
            <div className="flex items-center justify-between gap-2 mb-4">
              <span className="text-sm font-medium text-gray-500">
                Section {index + 1}
              </span>
              <button
                type="button"
                onClick={() => removeSection(index)}
                className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                title="Remove section"
              >
                <Trash2 size={18} />
              </button>
            </div>
            <div className="grid gap-4">
              <TextInput
                label="Title"
                value={section.title || ""}
                onChange={(e) => updateSection(index, "title", e.target.value)}
                placeholder="Section title"
                error={
                  section.titleTouched &&
                  !section.title?.trim() &&
                  "Title is required"
                }
                required
              />
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Icon (for display)
                </label>
                <select
                  value={section.iconName || "Shield"}
                  onChange={(e) =>
                    updateSection(index, "iconName", e.target.value)
                  }
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                >
                  {ICON_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <TextInput
                label="Content"
                value={section.content || ""}
                onChange={(e) =>
                  updateSection(index, "content", e.target.value)
                }
                placeholder="Section content"
                error={
                  section.contentTouched &&
                  !section.content?.trim() &&
                  "Content is required"
                }
                multiline
                rows={3}
                required
              />
              {(section.listItems || []).length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    List items
                  </label>
                  <div className="space-y-2">
                    {(section.listItems || []).map((item, i) => (
                      <div key={i} className="flex gap-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) =>
                            updateListItem(index, i, e.target.value)
                          }
                          className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                          placeholder="-"
                        />
                        <button
                          type="button"
                          onClick={() => removeListItem(index, i)}
                          className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => addListItem(index)}
                    className="mt-2 text-sm text-primary-600 hover:underline"
                  >
                    + Add list item
                  </button>
                </div>
              )}
              {(!section.listItems || section.listItems.length === 0) && (
                <button
                  type="button"
                  onClick={() => addListItem(index)}
                  className="text-sm text-primary-600 hover:underline"
                >
                  + Add bullet list (optional)
                </button>
              )}
            </div>
          </div>
        ))}
        <div className="flex gap-4 mt-8 items-stretch">
          <button
            type="button"
            onClick={addSection}
            className="w-[70%] flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/50 transition-colors"
          >
            <Plus size={20} />
            Add section
          </button>
          <div className="w-[30%]">
            <Button
              variant="primary"
              onClick={handleSave}
              className="w-full shadow-md"
              disabled={saving || !isChanged || isFormInvalid}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCMSNonRefundPolicy;
